import React, { useState, useEffect, useRef, useCallback } from 'react';
import clsx from 'clsx';
import { ConnectedHeader } from '@/features/shared/feature-connected-header';
import { SearchBarHeader } from '../search-bar';
import { SearchRoomContextProvider } from '../../hooks/use-search-room';
import { useHeaderVisibility } from '@/contexts/header-visibility-context';
import { useTranslation } from 'react-i18next';
import { ReactSVG } from 'react-svg';

interface MarketPlaceHeaderProps {
  pathname?: string; // Optional: can be passed from Rails server
}

const HOME_PATHS = ['/', '/en', '/fr', '/nl', '/en/', '/fr/', '/nl/'];

const MarketPlaceHeader: React.FC<MarketPlaceHeaderProps> = ({ pathname }) => {
  const { t } = useTranslation();

  const resolvedPathname = pathname ?? (typeof window !== 'undefined' ? window.location.pathname : '/');
  const isHomePage = HOME_PATHS.includes(resolvedPathname);

  const [isScrolled, setIsScrolled] = useState(!isHomePage);
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const { isHeaderVisible } = useHeaderVisibility();

  const scrollThresholdRef = useRef(
    typeof window !== 'undefined' ? window.innerHeight * 0.7 : 500
  );

  const handleCloseSearch = useCallback(() => {
    setIsSearchExpanded(false);
  }, []);

  useEffect(() => {
    if (!isScrolled && isSearchExpanded) {
      setIsSearchExpanded(false);
    }
  }, [isScrolled]);

  useEffect(() => {
    if (!isSearchExpanded) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleCloseSearch();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isSearchExpanded, handleCloseSearch]);

  useEffect(() => {
    if (!isHomePage || typeof window === 'undefined') return;

    const handleResize = () => {
      scrollThresholdRef.current = window.innerHeight * 0.7;
    };

    // Correct the SSR fallback immediately on mount
    handleResize();

    window.addEventListener('resize', handleResize, { passive: true });
    return () => window.removeEventListener('resize', handleResize);
  }, [isHomePage]);

  useEffect(() => {
    if (!isHomePage || typeof window === 'undefined') return;

    const handleScroll = () => {
      setIsScrolled(window.scrollY > scrollThresholdRef.current);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Initial check in case the page is already scrolled
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isHomePage]);

  if (!isHeaderVisible) {
    return null;
  }

  return (
    <SearchRoomContextProvider>
      <ConnectedHeader>
        <ConnectedHeader.Trailing>
          <div className="hidden md:block flex-1">
            {isScrolled && (
              !isSearchExpanded ? (
                  <button
                    onClick={() => setIsSearchExpanded(true)}
                    aria-label={t('js.features.marketplace.components.room_search.open_search')}
                    aria-expanded={isSearchExpanded}
                    className={clsx(
                      'flex items-center justify-center',
                      'px-3 py-2',
                      'rounded-full border border-border-primaryLight/60',
                      'transition-opacity hover:opacity-70'
                    )}
                  >
                    <ReactSVG
                      src={require('@assets/images/marketplace/header/Search.svg')}
                      aria-hidden="true"
                      beforeInjection={(svg) => {
                        svg.setAttribute('aria-hidden', 'true');
                        svg.setAttribute('width', '24');
                        svg.setAttribute('height', '24');
                      }}
                    />
                  </button>
                ) : (
                  <SearchBarHeader autoFocus onClose={handleCloseSearch} />
                )
            )}
          </div>
        </ConnectedHeader.Trailing>
        <ConnectedHeader.MobileSideMenuLeading>
          <SearchBarHeader className="searchbar-mobile" />
        </ConnectedHeader.MobileSideMenuLeading>
      </ConnectedHeader>
    </SearchRoomContextProvider>
  );
};

export { MarketPlaceHeader };
export type { MarketPlaceHeaderProps };
